import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { ConstantActions } from "../../../Services/_actions";
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import DynamicBreadcrumbs from "../../Shared/Breadcrumb/index.tsx";
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from "react-router";

interface SignupPayload {
  code?: string;
  phone_number_id?: string;
  waba_id?: string;
  event_type?: string;
  current_step?: string;
  error_message?: string;
}


const EmbeddedSignup: React.FC = () => {
  const actions = ConstantActions();
  const navigate = useNavigate();
  const [payload, setPayload] = useState<SignupPayload>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasAttemptedSubmission, setHasAttemptedSubmission] = useState(false);
  const { showSnackBar } = useSnackBar();
  useEffect(() => {
    const initializeFacebookSDK = () => {
      window.fbAsyncInit = function () {
        window.FB.init({
          appId: "1222044378809517",
          autoLogAppEvents: true,
          xfbml: true,
          version: "v21.0",
        });
      };

      (function (d, s, id) {
        let js: any,
          fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s);
        js.id = id;
        js.src = "https://connect.facebook.net/en_US/sdk.js";
        fjs.parentNode?.insertBefore(js, fjs);
      })(document, "script", "facebook-jssdk");
    };

    initializeFacebookSDK();

    const handleMessageEvent = (event: MessageEvent) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          if (data.event === "FINISH") {
            const { phone_number_id, waba_id } = data.data;
            setPayload((prev) => ({
              ...prev,
              phone_number_id,
              waba_id,
              event_type: data.event,
              current_step: undefined,
              error_message: undefined,
            }));
            showSnackBar("Signup completed successfully!", "success");
          } else if (data.event === "CANCEL") {
            const { current_step } = data.data;
            setPayload((prev) => ({ ...prev, current_step, event_type: data.event }));
            showSnackBar(`Signup canceled at step: ${current_step || "unknown"}`, "warning");
          } else if (data.event === "ERROR") {
            const { error_message } = data.data;
            setPayload((prev) => ({ ...prev, error_message, event_type: data.event }));
            showSnackBar(error_message || "Meta signup returned an error.", "error");
          }
        }
      } catch (error) {
        console.error("Error processing message:", error);
      }
    };

    window.addEventListener("message", handleMessageEvent);

    return () => {
      window.removeEventListener("message", handleMessageEvent);
    };
  }, []);

  const sendAccessTokenFromBackend = async (data: SignupPayload) => {
    const { code, phone_number_id, waba_id } = data;
    if (!code || !phone_number_id || !waba_id) {
      return;
    }

    try {
      setHasAttemptedSubmission(true);
      setIsSubmitting(true);
      await actions.postFacebookData({ code, phone_number_id, waba_id });
      setHasSubmitted(true);
      showSnackBar("Facebook data sent successfully.", "success");
    } catch (error) {
      console.error(error, "Error while sending data to backend");
      showSnackBar("Failed to send data. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fbLoginCallback = (response: any) => {
    console.log("FB Login Response:", response);
    if (response.authResponse) {
      const code = response.authResponse.code;
      setHasSubmitted(false);
      setHasAttemptedSubmission(false);
      setPayload((prev) => ({ ...prev, code }));
    } else {
      setPayload((prev) => ({ ...prev, code: undefined }));
      showSnackBar("User did not authorize or canceled login.", "error");
      console.error("User did not fully authorize or canceled login.");
    }
  };

  useEffect(() => {
    if (hasSubmitted || isSubmitting || hasAttemptedSubmission) {
      return;
    }

    if (payload.code && payload.waba_id && payload.phone_number_id) {
      sendAccessTokenFromBackend(payload);
    }
  }, [payload, hasSubmitted, isSubmitting, hasAttemptedSubmission]);

  const canSubmit = useMemo(
    () => Boolean(payload.code && payload.waba_id && payload.phone_number_id),
    [payload.code, payload.waba_id, payload.phone_number_id]
  );

  const launchWhatsAppSignup = () => {
    window.FB.login(fbLoginCallback, {
      config_id: "505651552549076",
      response_type: "code",
      override_default_response_type: true,
      // redirect_uri: "https://0ff1-103-180-80-202.ngrok-free.app/r/setting/meta-embedded-signup",
      extras: {
        setup: {},
        featureType: "",
        sessionInfoVersion: "2",
      },
    });
  };

  const breadcrumbsData = [
    { label: <IconButton color='primary'><HomeIcon /></IconButton>, onClick: () => navigate("/") },
    { label: 'Meta Embedded Signup' },
  ];

return (
  <Box sx={{ px: 3, py: 2 }}>
    <DynamicBreadcrumbs
      items={breadcrumbsData}
      onBreadcrumbClick={(event, item) => {
        if (item?.onClick) {
          item.onClick();
        }
      }}
    />

    {/* Header */}
    <Box sx={{ textAlign: "center", mb: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        Meta Embedded Signup
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Connect your WhatsApp Business account via Facebook
      </Typography>
    </Box>

    {/* Card */}
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Card
        sx={{
          width: "100%",
          maxWidth: 700,
          borderRadius: 3,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          
          {/* Login Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={launchWhatsAppSignup}
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "16px",
            }}
          >
            Login with Facebook
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* Session Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Session Information
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#f9fafb",
                fontFamily: "monospace",
                fontSize: "14px",
              }}
            >
              <Typography>
                <strong>Phone Number ID:</strong>{" "}
                {payload.phone_number_id || "N/A"}
              </Typography>
              <Typography>
                <strong>WABA ID:</strong> {payload.waba_id || "N/A"}
              </Typography>
            </Box>
          </Box>

          {/* SDK Response */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              SDK Response
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#f9fafb",
                fontFamily: "monospace",
                fontSize: "14px",
              }}
            >
              <Typography>
                <strong>Code:</strong> {payload.code || "N/A"}
              </Typography>
              <Typography>
                <strong>Ready to Submit:</strong>{" "}
                {canSubmit ? "Yes" : "No"}
              </Typography>
            </Box>
          </Box>

        </CardContent>
      </Card>
    </Box>
  </Box>
);
};

export default EmbeddedSignup;
